import { api } from "@/openapi/client";
import { operations } from "@/openapi/generated";
import { ApiResponseError } from "@/lib/error";
import { getCacheTags } from "@/lib/cache-utils";
import { projectQueryKeys } from "@/app/[locale]/projects/_api/project-query-keys";

// The generated types only include page/size/sort. Extend with filter params
// that are defined in the BE but not yet regenerated in generated.ts.
type GetProjectsBaseParameter = operations["getProjectsForCertifiedCharities"]["parameters"]["query"];
export type GetProjectsRequestParameter = GetProjectsBaseParameter & {
    fieldOfWorkId?: string;
    popular?: boolean;
};
export type GetProjectsResponse = operations["getProjectsForCertifiedCharities"]["responses"]["200"]["content"]["*/*"]

export async function getProjects(query: GetProjectsRequestParameter = {}): Promise<GetProjectsResponse> {
    const {data, error} = await api.GET("/projects/withAssociations",
        {
            // Cast needed because the generated query type doesn't yet include
            // fieldOfWorkId / countries / popular — these will be added on next regen.
            params : { query: query as GetProjectsBaseParameter },
            next: {tags: getCacheTags(projectQueryKeys.list(query))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getProjects");

    return data;
}

export type GetProjectsByFieldOfWorkRequest = operations["getCertifiedProjectsByField"]["parameters"]["query"]
export type GetProjectsByFieldOfWorkResponse = operations["getCertifiedProjectsByField"]["responses"]["200"]["content"]["*/*"]

export async function getProjectsByFieldOfWork(query: GetProjectsByFieldOfWorkRequest): Promise<GetProjectsByFieldOfWorkResponse> {
  const {data, error} = await api.GET("/projects/withAssociations/byFieldOfWork",
    {
      params : { query },
      next: {tags: getCacheTags(projectQueryKeys.byFieldOfWork(query))}
    });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getProjects");

  return data;
}


export type SearchProjectsRequest = operations["search"]["parameters"]["query"]
export type SearchProjectsResponse =  operations["search"]["responses"]["200"]["content"]["*/*"]

// Search in name, description, charity name, field of work
export async function searchProjects(query : SearchProjectsRequest): Promise<SearchProjectsResponse> {
    const {data, error} = await api.GET("/projects/search",
        {
            params : { query },
            next: {tags: getCacheTags(projectQueryKeys.search(query))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("searchProjects");

    return data;
}

export type GetProjectByIdRequestParameter = string;
export type GetProjectByIdResponse = operations["getItemResource-project-get"]["responses"]["200"]["content"]["*/*"];

export async function getProjectById(id: GetProjectByIdRequestParameter): Promise<GetProjectByIdResponse> {
    const {data, error} = await api.GET("/projects/{id}",
        {
            params: { path: { id } },
            next: {tags: getCacheTags(projectQueryKeys.detail(id))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getProjectById");

    return data;
}

export type GetProjectDetailRequestParameter = string;
export type GetProjectDetailResponse = operations["getProjectDetail"]["responses"]["200"]["content"]["*/*"];

export async function getProjectDetail(id: GetProjectDetailRequestParameter): Promise<GetProjectDetailResponse> {
    const {data, error} = await api.GET("/projects/{id}/detail",
        {
            params: { path: { id } },
            next: {tags: getCacheTags(projectQueryKeys.detail(id))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getProjectDetail");

    return data;
}

export type GetProjectBySlugRequestParameter = operations["getProjectBySlug"]["parameters"]["path"]["slug"];
export type GetProjectBySlugResponse = operations["getProjectBySlug"]["responses"]["200"]["content"]["*/*"];

export async function getProjectBySlug(slug: GetProjectBySlugRequestParameter): Promise<GetProjectBySlugResponse> {
    // PG slug comparison is case-sensitive — normalize before passing in.
    const normalizedSlug = slug.toLowerCase();
    const {data, error} = await api.GET("/projects/bySlug/{slug}",
        {
            params: { path: { slug: normalizedSlug } },
            next: {tags: getCacheTags(projectQueryKeys.detailBySlug(normalizedSlug))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getProjectBySlug");

    return data;
}

export type GetProjectDetailBySlugRequestParameter = operations["getProjectDetailBySlug"]["parameters"]["path"]["slug"];
export type GetProjectDetailBySlugResponse = operations["getProjectDetailBySlug"]["responses"]["200"]["content"]["*/*"];

export async function getProjectDetailBySlug(slug: GetProjectDetailBySlugRequestParameter): Promise<GetProjectDetailBySlugResponse> {
    // PG slug comparison is case-sensitive — normalize before passing in.
    const normalizedSlug = slug.toLowerCase();
    const {data, error} = await api.GET("/projects/bySlug/{slug}/detail",
        {
            params: { path: { slug: normalizedSlug } },
            next: {tags: getCacheTags(projectQueryKeys.detailBySlug(normalizedSlug))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getProjectDetailBySlug");

    return data;
}

export type GetProjectSupportersRequestParameter = {
    id: string;
    query?: {
        page?: number;
        size?: number;
    };
};
export type GetProjectSupportersResponse = operations["getProjectSupporters"]["responses"]["200"]["content"]["*/*"];

export async function getProjectSupporters({id, query}: GetProjectSupportersRequestParameter): Promise<GetProjectSupportersResponse> {
    const {data, error} = await api.GET("/projects/{id}/supporters",
        {
            params: { 
                path: { id },
                query: { pageable: query || {} }
            },
            next: {tags: getCacheTags(projectQueryKeys.supporters(id, query))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getProjectSupporters");

    return data;
}

export type GetProjectStatisticsRequestParameter = string;
export type GetProjectStatisticsResponse = operations["getProjectStatistics"]["responses"]["200"]["content"]["*/*"];

export async function getProjectStatistics(id: GetProjectStatisticsRequestParameter): Promise<GetProjectStatisticsResponse> {
    const {data, error} = await api.GET("/projects/{id}/statistics",
        {
            params: { path: { id } },
            next: {tags: getCacheTags(projectQueryKeys.statistics(id))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getProjectStatistics");

    return data;
}

export type GetProjectTransactionsRequestParameter = {
    id: string;
    query?: {
        page?: number;
        size?: number;
    };
};
export type GetProjectTransactionsResponse = operations["getProjectTransactions"]["responses"]["200"]["content"]["*/*"];

export type GetRelatedProjectsRequestParameter = string;
export type GetRelatedProjectsResponse = operations["getRelatedProjects"]["responses"]["200"]["content"]["*/*"];

export async function getRelatedProjects(id: GetRelatedProjectsRequestParameter): Promise<GetRelatedProjectsResponse> {
    const {data, error} = await api.GET("/projects/{id}/related",
        {
            params: { path: { id } },
            next: {tags: getCacheTags(projectQueryKeys.related(id))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getRelatedProjects");

    return data;
}

export async function getProjectTransactions({id, query}: GetProjectTransactionsRequestParameter): Promise<GetProjectTransactionsResponse> {
    const {data, error} = await api.GET("/projects/{id}/transactions",
        {
            params: { 
                path: { id },
                query: { pageable: query || {} }
            },
            next: {tags: getCacheTags(projectQueryKeys.transactions(id, query))}
        });
    if (error) throw error;
    if (!data) throw new ApiResponseError("getProjectTransactions");

    return data;
}
